import { useMemo } from 'react';
import { useAppStore } from '../store';
import { useReportStore } from '../store/reportStore';
import {
  getRootCategories,
  getCategoryDescendants,
  getTargetCategoryId,
} from '../services/categoryService';
import { CHART_COLOR_PALETTE } from '../components/charts/constants';
import type { Transaction } from '../types';

export interface CategorySpending {
  categoryId: string;
  categoryName: string;
  amount: number;
  color: string;
  transactions: Transaction[];
}

export interface CategorySpendingComparisonData {
  data: CategorySpending[];
}

export function useCategorySpendingComparison(): CategorySpendingComparisonData {
  const transactions = useAppStore((state) => state.transactions);
  const categories = useAppStore((state) => state.categories);
  const dateRange = useReportStore((state) => state.dateRange);
  const selectedCategoryIds = useReportStore(
    (state) => state.selectedCategoryIds
  );

  return useMemo(() => {
    const selectedIdSet = new Set(selectedCategoryIds);

    const displayCategories =
      selectedCategoryIds.length > 0
        ? selectedCategoryIds
            .map((id, index) => {
              const cat = categories.find((c) => c.id === id);
              return cat
                ? {
                    id: cat.id,
                    name: cat.name,
                    color:
                      CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length],
                  }
                : null;
            })
            .filter(
              (c): c is { id: string; name: string; color: string } =>
                c !== null
            )
        : getRootCategories(categories).map((cat, index) => ({
            id: cat.id,
            name: cat.name,
            color: CHART_COLOR_PALETTE[index % CHART_COLOR_PALETTE.length],
          }));

    const allowedCategoryIds = new Set<string>();
    if (selectedCategoryIds.length > 0) {
      selectedCategoryIds.forEach((id) => {
        allowedCategoryIds.add(id);
        const descendants = getCategoryDescendants(categories, id);
        descendants.forEach((d) => allowedCategoryIds.add(d.id));
      });
    }

    const spendingByCategory: Record<string, number> = {};
    const transactionsByCategory: Record<string, Transaction[]> = {};
    displayCategories.forEach((cat) => {
      spendingByCategory[cat.id] = 0;
      transactionsByCategory[cat.id] = [];
    });

    const categoryMap = new Map(categories.map((c) => [c.id, c]));

    transactions.forEach((transaction) => {
      if (transaction.type !== 'expense') return;
      if (transaction.excludedFromBudget) return;

      const transactionDate = transaction.date;
      if (transactionDate < dateRange.start || transactionDate > dateRange.end)
        return;

      if (
        allowedCategoryIds.size > 0 &&
        transaction.categoryId &&
        !allowedCategoryIds.has(transaction.categoryId)
      )
        return;

      const targetCategoryId = getTargetCategoryId(
        transaction.categoryId,
        categories,
        selectedIdSet,
        categoryMap
      );
      if (!targetCategoryId) return;
      if (!displayCategories.some((c) => c.id === targetCategoryId)) return;

      spendingByCategory[targetCategoryId] =
        (spendingByCategory[targetCategoryId] || 0) +
        Math.abs(transaction.amount);
      transactionsByCategory[targetCategoryId]?.push(transaction);
    });

    const data: CategorySpending[] = displayCategories
      .map((cat) => ({
        categoryId: cat.id,
        categoryName: cat.name,
        amount: spendingByCategory[cat.id] || 0,
        color: cat.color,
        transactions: transactionsByCategory[cat.id] || [],
      }))
      .filter((item) => item.amount > 0)
      .sort((a, b) => b.amount - a.amount);

    return { data };
  }, [transactions, categories, dateRange, selectedCategoryIds]);
}
